import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  // TablePagination,
  Typography,
  Paper,
  Card,
  Button,
  CircularProgress,
  TextField,
  useMediaQuery,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { debounce } from "lodash";
import apis from "../utils/apis";
import Header from "../components/Header";
import { getEmail, saveTargetPath } from "../utils/storage";
import toast, { Toaster } from "react-hot-toast";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import CollapsibleTableNew from "../components/common/CollapsibleTableNew";
import CollapsibleTable from "../components/common/CollapsibleTable";
import CollapsibleTableMobile from "../components/common/CollapsibleTableMobile";
import axios from "axios";
import Pagination from "@mui/material/Pagination";
import TablePagination from "../components/common/TablePagination";

// Styled components for table
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: "#FDF9E0",
  borderColor: "#FDF9E0",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(39, 140, 55, 0.1)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "transparent",
  },
}));

const formatISTDateTime = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const RecruiterResumeHistory = () => {
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [sortOrder, setSortOrder] = useState("");
  const isMobile = useMediaQuery("(max-width:600px)");
  const isMobileOrTablet = useMediaQuery("(max-width:960px)");
  const navigate = useNavigate();

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const handleSearchChange = debounce((event) => {
    const value = event.target.value.toLowerCase();
    setSearchQuery(escapeRegExp(value));

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (value.trim().length === 0) {
      setTableLoading(true);
      const timeout = setTimeout(() => {
        fetchSearchResults(value, 1, rowsPerPage, sortOrder);
      }, 3000);
      setSearchTimeout(timeout);
    } else if (value.trim().length > 2) {
      fetchSearchResults(value, 1, rowsPerPage, sortOrder);
    } else {
      setPage(0);
      fetchSearchResults(value, 1, rowsPerPage, sortOrder);
    }
  }, 300);

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const cache = useRef({});
  const cancelTokenSource = useRef(null);

  const fetchData = async (currentPage, limit, isTableLoading = false) => {
    if (cache.current[currentPage]) {
      setTableLoading(false);
      setData(cache.current[currentPage].data);
      setTotalCount(cache.current[currentPage].totalCount);
      return;
    }

    if (isTableLoading) setTableLoading(true);
    else setLoading(true);

    try {
      const response = await apis.GET({
        endpoint: `/get-recruiter-history?page=${currentPage}&limit=${limit}`,
      });
      if (response.status === 201) {
        const pageData = response.data.data || [];
        const totalCount = response.data.totalCount || 0;
        cache.current[currentPage] = { data: pageData, totalCount };
        setData(pageData);
        setTotalCount(totalCount);
        setTableLoading(false);
        setLoading(false);
      } else {
        setTableLoading(false);
        setLoading(false);

        throw new Error("Failed to fetch data");
      }
    } catch (err) {
      if (isTableLoading) setTableError("Failed to load table data.");
      else setError("Failed to load data. Please try again later.");
    } finally {
      if (isTableLoading) setTableLoading(false);
      else setLoading(false);
    }
  };

  const fetchSearchResults = async (
    searchText,
    pageNum = 1,
    limitNum = rowsPerPage,
    sortOrder
  ) => {
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel(
        "Operation canceled due to new request."
      );
    }

    cancelTokenSource.current = apis.getCancelTokenSource();

    setTableLoading(true);
    try {
      const response = await apis.GET({
        endpoint: `/get-recruiter-history-search?searchText=${encodeURIComponent(
          searchText
        )}&page=${pageNum}&limit=${limitNum}&sortField=name&sortOrder=${sortOrder}`,
        cancelToken: cancelTokenSource.current.token,
      });
      if (response.status === 201) {
        const pageData = response.data.data || [];
        const totalCount = response.data.totalCount || 0;
        setData(pageData);
        setTotalCount(totalCount);
        setPage(pageNum - 1);
        setTableLoading(false);
      } else {
        setTableLoading(false);
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("Request canceled:", err.message);
      } else {
        setData([]);
        setTotalCount(0);
        setTableError("Failed to load search results.");
        setTableLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData(1, rowsPerPage);
    fetchSearchResults(searchQuery, 1, rowsPerPage, sortOrder);
  }, []);

  useEffect(() => {}, [sortOrder]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchSearchResults(searchQuery, newPage + 1, rowsPerPage, sortOrder);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    cache.current = {};
    fetchSearchResults(searchQuery, 1, newRowsPerPage, sortOrder);
  };

  const handleSortOrderChange = (event) => {
    const order = event.target.value;
    setSortOrder(order);
    fetchSearchResults(searchQuery, 1, rowsPerPage, order);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        minHeight: "100vh",
        width: "100%",
        color: "#FDF9E0",
        backgroundImage: "url('../../assets/images/homepage-background.svg')",
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: isMobile ? 2 : 4,
      }}
    >
      <Toaster />
      <Header />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress size={50} />
          <Typography sx={{ mt: 2, textAlign: "center" }}>
            Loading...
          </Typography>
        </Box>
      ) : error ? (
        <Typography sx={{ textAlign: "center", color: "red" }}>
          {error}
        </Typography>
      ) : (
        <Box>
          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              mt: { xs: 10, md: 3 },
              mb: { xs: 2, md: 2 },
              fontWeight: 700,
              fontFamily: "Kanit",
            }}
          >
            View History
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 2 : 3,
              alignItems: isMobile ? "center" : "flex-end",
            }}
          >
            <TextField
              variant="outlined"
              placeholder="Search by Name, Date, Years of Experience, Location, Last Company, Last Role"
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#26801E" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: isMobile ? "100%" : "50%",
                borderRadius: "5px",
                borderColor: "primary",
                "& .MuiInputBase-root": {
                  backgroundColor: "#FDF9E0",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#FDF9E0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#26801E",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#26801E",
                  },
                },
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              mb: 2,
              mx: { xs: 2, md: 0, lg: 0, xl: 11.8 },
            }}
          >
            {/* <Tooltip title="Sort resumes by name"> */}
              <FormControl sx={{ minWidth: isMobile ? "100%" : 200 }}>
                <InputLabel
                  sx={{
                    color: "#FDF9E0",
                  }}
                >
                  Sort By Name
                </InputLabel>
                <Select
                  value={sortOrder}
                  onChange={handleSortOrderChange}
                  label="Sort By Name"
                  sx={{
                    color: "#FDF9E0",
                    backgroundColor: "rgba(39, 140, 55, 0.2)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#FDF9E0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#26801E",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#26801E",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#FDF9E0",
                    },
                  }}
                >
                  <MenuItem value="asc">Name (A-Z)</MenuItem>
                  <MenuItem value="desc">Name (Z-A)</MenuItem>
                </Select>
              </FormControl>
            {/* </Tooltip> */}
          </Box>
          <Paper
            sx={{
              backgroundColor: "transparent",
            }}
          >
            <Box
              sx={{
                position: "relative",
                maxWidth: {
                  xs: "100%",
                  md: "100%",
                  xl: "90%",
                },
                margin: "0 auto",
              }}
            >
              <TableContainer
                sx={{
                  overflowX: "auto",
                  "&::-webkit-scrollbar": {
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    margin: "2px",
                    background: "#231F20",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#FDF9E0",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#26801E",
                  },
                }}
              >
                <CollapsibleTableNew
                  rows={data}
                  tableLoading={tableLoading}
                  searchQuery={searchQuery}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  collapsible={true}
                  tableName={"resume-history"}
                />
              </TableContainer>
              <TablePagination
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={totalCount}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                  tableLoading={tableLoading}

              />             
            </Box>
          </Paper>
        </Box>
      )}
      <Box
        sx={{ display: "flex", justifyContent: "center", mt: { xs: 2, md: 0 } }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/Upload-Resume")}
          sx={{
            color: "#FDF9E0",
            textTransform: "none",
            borderRadius: "8px",
            padding: "10px 20px",
            "&:hover": {
              backgroundColor: "#1E6A16",
            },
          }}
        >
          Back to Upload
        </Button>
      </Box>
    </Box>
  );
};

export default RecruiterResumeHistory;
